if (!customElements.get('main-product-gallery')) {
  customElements.define(
    'main-product-gallery',
    class MainProductGallery extends HTMLElement {
      constructor() {
        super();

        this.mainCarouselSelector = '#main-carousel';
        this.thumbnailCarouselSelector = '#thumbnail-carousel';

        this.variants = this.getVariants();

        this.init();
        this.syncedVariantImage = this.dataset.syncedVariantImage;
        this.variantShouldTrigger = true;
      }

      connectedCallback() {
        subscribe(PUB_SUB_EVENTS.variantChange, this.handleVariantChange.bind(this));
      }

      init() {
        // Remember the variant that is selected when the page loads: the gallery must stay
        // on the featured (main) image and only navigate when the visitor actively picks
        // another variant. See handleVariantChange().
        try {
          const selectedVariantJson = this.closest('product-info')?.querySelector('variant-selects [data-selected-variant]')?.innerHTML;
          this.initialVariantId = selectedVariantJson ? JSON.parse(selectedVariantJson)?.id : null;
        } catch (error) {
          this.initialVariantId = null;
        }
        this.initialVariantSyncPending = true;

        this.convertVariantsToArray();

        this.mainSlider = new Splide(this.querySelector(this.mainCarouselSelector), {
          type: 'loop',
          rewind: true,
          pagination: false,
          arrows: false,
          autoHeight: true,
          perPage: 1,
          heightRatio: 1,
        });

        if (window.matchMedia('(max-width: 600px)').matches) {
          if (this.querySelector(this.thumbnailCarouselSelector).dataset.showArrowsMobile === 'hide') {
            this.querySelector(this.thumbnailCarouselSelector).classList.add('hidden');
          } else {
            this.querySelector(this.thumbnailCarouselSelector).classList.remove('hidden');
          }
        } else {
          this.querySelector(this.thumbnailCarouselSelector).classList.remove('hidden');
        }

        this.thumbnailSlider = new Splide(this.querySelector(this.thumbnailCarouselSelector), {
          perPage: 5,
          gap: 10,
          pagination: false,
          dragMinThreshold: 10,
          isNavigation: true,
          arrows: this.querySelector(this.thumbnailCarouselSelector).dataset.showArrows === 'true',
          classes: {
            arrow: 'slider-button splide__arrow',
          },
          // Vertical thumbnails only on desktop (>= 990px), horizontal below. Splide v4 applies breakpoints like media queries, also on window resize.
          mediaQuery: 'min',
          breakpoints: {
            990: {
              direction: 'ttb',
              // Fixed track height = 5 square slides (column width 9rem) + 4 gaps (10px each),
              // so every thumbnail slide is exactly as high as it is wide (square, no cropping).
              height: 'calc(45rem + 40px)',
              wheel: true,
              releaseWheel: true,
            },
          },
        });

        this.mainSlider.sync(this.thumbnailSlider);

        // Sets --active-slide-height from the active slide's real image height. The value must
        // be refreshed until the page layout has fully settled, otherwise the gallery can lock
        // itself to a too small height and the main image looks cut off.
        this.updateActiveSlideHeight = () => {
          const currentSlide = this.mainSlider.Components.Elements.slides[this.mainSlider.index];
          const slideImage = currentSlide ? currentSlide.querySelector('img') : null;

          if (slideImage && slideImage.offsetHeight > 0) {
            this.style.setProperty('--active-slide-height', `${slideImage.offsetHeight}px`);
          }
        };

        // The height is deliberately not locked until the page has fully loaded: earlier
        // measurements happen while the flex layout and the thumbnail slider are still
        // settling and would freeze a too small height (main image cut off). Before that
        // the slides simply use their natural size.
        window.addEventListener('load', () => {
          this.updateActiveSlideHeight();

          if ('ResizeObserver' in window) {
            this.galleryResizeObserver = new ResizeObserver(() => this.updateActiveSlideHeight());
            this.galleryResizeObserver.observe(this.querySelector(this.mainCarouselSelector));
          }
        });

        // Keep both carousels hidden until the page has fully loaded. During mount Splide
        // inserts the loop clones (copies of the last slides in front of the first one) and
        // for a moment they are visible on the left before everything is positioned.
        const mainCarousel = this.querySelector(this.mainCarouselSelector);
        const thumbnailCarousel = this.querySelector(this.thumbnailCarouselSelector);
        mainCarousel.style.visibility = 'hidden';
        thumbnailCarousel.style.visibility = 'hidden';

        this.mainSlider.mount();
        this.thumbnailSlider.mount();

        window.addEventListener('load', () => {
          mainCarousel.style.visibility = '';
          thumbnailCarousel.style.visibility = '';
        });

        this.mainSlider.on('move', (newIndex) => {
          const currentSlide = newIndex;
          const currentSlideElement = this.mainSlider.Components.Elements.slides;

          if (currentSlideElement) {
            const currentSlideItem = currentSlideElement[currentSlide];
            const slideImage = currentSlideItem ? currentSlideItem.querySelector('img') : null;
            if (slideImage && slideImage.offsetHeight > 0) {
              this.style.setProperty('--active-slide-height', `${slideImage.offsetHeight}px`);
            }
          }
        });

        if (Debutify.settings.dbtfy_variant_sync_on_media) {
          this.thumbnailSlider.on('moved', (newIndex) => {
            if (!this.variantShouldTrigger) {
              this.variantShouldTrigger = true;
              return;
            }
            this.variantShouldTrigger = true;

            const mediaId = this.thumbnailSlider.Components.Elements.slides[newIndex].dataset.mediaId;

            const currentVariantOptions = this.variants[mediaId] || null;

            if (currentVariantOptions) {
              this.syncedVariantImage = 'false'; // temporary flag to prevent triggering the event

              const variantSelects = this.closest('product-info').querySelectorAll('variant-selects fieldset');

              currentVariantOptions.forEach((option, index) => {
                const variantSelect = variantSelects[index];
                if (variantSelect) {
                  const radio = variantSelect.querySelector(`input[value="${option}"]`);
                  if (radio) {
                    radio.checked = true;
                    radio.dispatchEvent(new Event('change', { bubbles: true }));
                  }
                }
              });
              this.syncedVariantImage = 'true';
            }
          });
        }
      }

      getVariants() {
        const productInfo = this.closest('product-info');
        const productVariants = productInfo.querySelector('variant-selects [data-product-variants]')?.innerHTML;
        return !!productVariants ? JSON.parse(productVariants) : null;
      }

      convertVariantsToArray() {
        if (!this.variants) return;

        this.variants = Object.entries(this.variants)
          .filter(([key, value]) => value.featured_media?.id) // Check if featured_media.id exists
          .reduce((acc, [key, value]) => {
            acc[value.featured_media.id] = value.options;
            return acc;
          }, {});
      }

      handleVariantChange({ data: { variant } }) {
        if (!variant.featured_media || this.syncedVariantImage !== 'true') return;

        // Ignore only the one automatic sync for the variant that is already selected on
        // page load, so the featured (main) image always stays the starting image and no
        // load-time slide animation flashes by. As soon as the visitor actively switches to
        // another variant, every later selection navigates the gallery normally again.
        if (this.initialVariantSyncPending) {
          if (variant.id === this.initialVariantId) return;
          this.initialVariantSyncPending = false;
        }

        const variantFeaturedMediaId = variant.featured_media.id;

        let mediaPosition = this.querySelector(this.thumbnailCarouselSelector)?.querySelector(
          "[data-media-id='" + `${variantFeaturedMediaId}` + "']"
        )?.dataset.position;

        if (mediaPosition) {
          mediaPosition = parseInt(mediaPosition) - 1;

          if (mediaPosition != this.thumbnailSlider.index) {
            this.variantShouldTrigger = false;
            this.thumbnailSlider?.go(mediaPosition);
          }
        }
      }
    }
  );
}
